import { buildGoogleAuthorizeUrl, createCodeChallenge, createRandomString } from "./cognito";

/** A problem start that was waiting on sign-in, replayed after the Google round trip. */
export interface PendingProblemStart {
  problemId: string;
  restart: boolean;
}

/** What the app remembers across the redirect to Google and back. */
export interface GoogleRedirectState {
  codeVerifier: string;
  state: string;
  returnPath: string;
  pendingProblem?: PendingProblemStart;
  /** Set once the callback has retried Cognito's first-link error. */
  retried?: boolean;
}

export const GOOGLE_REDIRECT_KEY = "sdp_google_sign_in";

export function saveGoogleRedirectState(value: GoogleRedirectState) {
  try {
    sessionStorage.setItem(GOOGLE_REDIRECT_KEY, JSON.stringify(value));
  } catch {
    // ignore: the callback will report the sign-in as expired
  }
}

export function loadGoogleRedirectState(): GoogleRedirectState | null {
  try {
    const raw = sessionStorage.getItem(GOOGLE_REDIRECT_KEY);
    return raw ? (JSON.parse(raw) as GoogleRedirectState) : null;
  } catch {
    return null;
  }
}

export function clearGoogleRedirectState() {
  try {
    sessionStorage.removeItem(GOOGLE_REDIRECT_KEY);
  } catch {
    // ignore
  }
}

/** Build the authorize URL for a saved redirect state (new or retried). */
export async function getGoogleAuthorizeUrl(redirect: GoogleRedirectState): Promise<string> {
  return buildGoogleAuthorizeUrl(await createCodeChallenge(redirect.codeVerifier), redirect.state);
}

/** Send the browser to Cognito's hosted sign-in for Google. */
export async function startGoogleSignIn(
  returnPath: string,
  pendingProblem?: PendingProblemStart,
  navigate: (url: string) => void = (url) => window.location.assign(url),
) {
  const redirect: GoogleRedirectState = {
    codeVerifier: createRandomString(32),
    state: createRandomString(16),
    returnPath,
    pendingProblem,
  };
  saveGoogleRedirectState(redirect);
  navigate(await getGoogleAuthorizeUrl(redirect));
}
